import {ErrorMessage, Field} from 'formik';
import _ from 'lodash';
import React from 'react';
import styled from 'styled-components';

import {FormControl} from '../../form-containers/FormControl';
import {FormContainer, FormGroup} from '../../form-containers/FormGroup';
import {formSearchInputSetup} from '../../form-control-features/formSearchInputSetup';
import {FormErrorMessage} from '../../form-labels/FormErrorMessage';

/**
 * @param {object} param0
 *
 * @param {string} [param0.name]
 * *This prop is applicable only for the Formik version of the control.*
 *
 * The name of the field in the Formik context.
 *
 * Ensure to include the `name` property when using a Formik-type form control. Without it, the form control may not function properly.
 * Additionally, remember to wrap `<Formik></Formik>` around this and all other form control types to ensure proper functionality.
 * Adding the name to a Formik Input component is essential because Formik uses the name prop to link the value of the  input to the Formik state.
 *
 * **Note that the Formik type is the default version injected into the form control you are applying.**
 *
 * @param {boolean} [param0.raw]
 * If set to true, a non-Formik version of the form control will be injected instead.
 *
 * @param {"sm"} [param0.size]
 * CSS variable names for adjusting the size of the form control you're using:
 *   - 'sm': Smaller variant of the form control type.
 *
 * @param {string} [param0.type]
 * CSS variable names to specify the type of input you want to use:
 *   - 'text': Default type for string input.
 *   - 'number': Specifies a number input type.
 *   - 'file': Indicates a file upload input type.
 *   - 'email': Specifies an input field for email addresses.
 *   - 'password': Specifies an input field for passwords.
 *   There are more standard input types which are currently not supported.
 *   - These types are also available, but it's not recommended to use them due to best practices:
 *      - 'date', 'datetime', 'time': Utilize dedicated components like <DayPicker/>, <DayTimePicker/>, <DoubleDatePicker/>, <PeriodDatePicker/>, <TimePicker/> or <WeekPicker/> instead.
 *      - 'checkbox', 'radio': Utilize dedicated components like <Radio />, <SingleCheckBox />, or <MultiCheckbox /> instead.
 *
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {React.ReactNode} [param0.prefix]
 * The text or icons to be displayed before the Input. Icons can be added using the following format:
 * prefix={<><Icon name='edit' /> -- additional text --</>}
 *
 * @param {React.ReactNode} [param0.statusBadge]
 * Specifies an extra label element to be displayed on the right side of the  input field.
 * Use the JSX syntax to include an icon element along with some text. For example: {<Icon name='edit' />} or {<>Custom text <Icon name='edit' /></>}
 *
 * @param {string} [param0.validationError]
 * Error message to display when input validation fails
 *
 * @param {React.ReactNode} [param0.suffix]
 * The text or icons to be displayed after the Input. Icons can be added using the following format:
 * suffix={<><Icon name='edit' /> -- additional text --</>}
 *
 * @param {boolean} [param0.autoFocus]
 * If true, React will focus the element on mount.
 *
 * @param {"on" | "off"} [param0.autoComplete]
 * Determines the autocomplete behavior of the input.
 * Accepted values: "on" (default),
 * Use "off" to disable autocomplete.
 *
 * @param {boolean} [param0.code]
 * If set to true, the font in the input will be changed to Monospace. Do this for things like Lua scripts.
 *
 * @param {Function} [param0.validate]
 * *This prop is applicable only for the Formik version of the control.*
 *
 * Function for input validation.
 *
 * @param {string} [param0.defaultValue]
 * *This prop is applicable only for the non-Formik version of the control.*
 *
 * Specifies the initial value for the input.
 *
 * @param {any} [param0.value]
 * Specifies the current value of the input field.
 * Meant for non-Formik Input (Raw).
 * This property sets the current value for a text input.
 *
 * @param {React.KeyboardEventHandler<HTMLInputElement>} [param0.onKeyDown]
 * Function to be called when a key is pressed down on the input.
 *
 * @param {React.KeyboardEventHandler<HTMLInputElement>} [param0.onKeyPress]
 * Function to be called when a key is pressed on the input.
 *
 * @param {React.KeyboardEventHandler<HTMLInputElement>} [param0.onKeyUp]
 * Function to be called when a key is released on the input.
 *
 * @param {React.FocusEventHandler<HTMLInputElement>} [param0.onBlur]
 * Function to be called when the input loses focus.
 *
 * @param {boolean} [param0.disabled]
 * Whether the input is disabled.
 *
 * @param {boolean} [param0.readOnly]
 * If set to `true` the input will be read-only.
 *
 * Compared to a `disabled` field, a `readOnly` field is still focusable and its value can be selected and copied.
 * Whereas, `disabled` fields are not focusable and generally do not function as controls until they are enabled
 *
 * @param {React.ChangeEventHandler<HTMLInputElement>} [param0.onChange]
 * This function will be invoked when the user changes the input value. This function is required for non-Formik inputs.
 * It is invoked immediately after the user changes the input value, ie. it fires on every keystroke.
 *
 * @param {string} [param0.placeholder]
 * For a text input, controls its text. (For a radio button, specifies its form data.)
 * Placeholder text follows a naming convention for consistency:
 *   - 'Add filter'
 *   - 'Type your name'
 *   - 'Select an option'
 *   - 'Pick an option'
 *   - 'Fill in your name'
 *
 * Avoid using placeholders like:
 *   - '--- type ---'
 *   - '--- select ---'
 *
 * @param {any} [param0.innerRef]
 *
 * @example
 * ```jsx
 *      // == --- How to use this component:  --- ==
 *      <Input label="name" name="name.user" placeholder='Type your name' />
 * ```
 */
export const Input = ({
    autoComplete,
    autoFocus,
    code,
    defaultValue,
    disabled,
    label,
    name,
    onBlur,
    onKeyDown,
    onKeyPress,
    onKeyUp,
    placeholder,
    prefix,
    raw,
    readOnly,
    size,
    statusBadge,
    suffix,
    type,
    validate,
    validationError,
    onChange,
    value,
    innerRef,
}) => {
    return (
        <Wrapper>
            {raw ? (
                <FormContainer
                    code={code}
                    disabled={disabled}
                    errorMessage={<>{validationError && <FormErrorMessage>{validationError}</FormErrorMessage>}</>}
                    label={label}
                    prefix={prefix}
                    suffix={suffix}
                    size={size}
                    statusBadge={statusBadge}>
                    <input
                        autoComplete={autoComplete}
                        autoFocus={autoFocus}
                        defaultValue={defaultValue}
                        disabled={disabled}
                        placeholder={placeholder}
                        readOnly={readOnly}
                        onBlur={onBlur}
                        onKeyDown={onKeyDown}
                        onKeyPress={onKeyPress}
                        onKeyUp={onKeyUp}
                        type={type}
                        onChange={onChange}
                        value={value}
                        ref={innerRef}
                    />
                </FormContainer>
            ) : (
                <FormContainer
                    code={code}
                    disabled={disabled}
                    errorMessage={<ErrorMessage name={name} component={FormErrorMessage} />}
                    label={label}
                    prefix={prefix}
                    suffix={suffix}
                    size={size}
                    statusBadge={statusBadge}>
                    <Field id={name} name={name} validate={validate}>
                        {({field}) => (
                            <input
                                {...field}
                                autoComplete={autoComplete}
                                autoFocus={autoFocus}
                                disabled={disabled}
                                placeholder={placeholder}
                                readOnly={readOnly}
                                type={type}
                                value={
                                    field.value ?? undefined // HTML inputs don't accept null values, so we coalesce to undefined instead.
                                }
                                onBlur={(event) => {
                                    // Notify consumers about the onBlur event
                                    _.attempt(onBlur, event);

                                    // Propagate the event to the Formik field
                                    field.onBlur(event);
                                }}
                                onChange={(event) => {
                                    // Notify consumers about the onChange event
                                    _.attempt(onChange, event);

                                    // Propagate the event to the Formik field
                                    field.onChange(event);
                                }}
                                onKeyDown={onKeyDown}
                                onKeyPress={onKeyPress}
                                onKeyUp={onKeyUp}
                                ref={innerRef}
                            />
                        )}
                    </Field>
                </FormContainer>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    width: 100%;
    ${formSearchInputSetup}
`;

Input.FormGroup = FormGroup;
Input.Container = FormControl;
